import classNames from 'classnames'
import { useRef, useState, type FC, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  LayoutItem,
  getLayoutIcon,
} from '@pages/IntroducingBroker/DashboardPoC/components/LayoutSelector/LayoutItem'
import type { DashboardLayout } from '@pages/IntroducingBroker/DashboardPoC/types'

import { Body2 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { HouseIcon } from '@ui/icons/House.icon'
import { MetricsIcon } from '@ui/icons/Metrics.icon'
import { PlusIcon } from '@ui/icons/Plus.icon'

import { useScrollEdges } from '@hooks/useScrollEdges'

import { Box } from '@shared/components/Box'

import styles from './LayoutSelector.module.scss'

export interface LayoutSelectorProps {
  layouts: DashboardLayout[]
  currentLayoutId: string
  defaultLayoutId: string
  handleSetDefaultLayout: (layoutId: string, isDefault?: boolean) => Promise<void>
  onLayoutSelect: (layoutId: string) => void
  onCreateNew: () => void
  onDeleteLayout: (layoutId: string) => void | Promise<void>
  canAddMore: boolean
  onEditMode: (id: string) => void
  openLayoutActionModal: (props: {
    actionEvent: 'save' | 'delete'
    onAccept: () => void | Promise<void>
    onCancel?: () => void
  }) => void
}

export const LayoutSelector: FC<LayoutSelectorProps> = ({
  layouts,
  currentLayoutId,
  onLayoutSelect,
  onCreateNew,
  onDeleteLayout,
  canAddMore,
  onEditMode,
  openLayoutActionModal,
  defaultLayoutId,
  handleSetDefaultLayout,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const popoverListRef = useRef<HTMLDivElement>(null)
  const { canScrollDown } = useScrollEdges(popoverListRef, undefined, [isOpen, layouts])

  const currentLayout = layouts.find(({ id }) => id === currentLayoutId)
  const presetLayouts = layouts.filter(({ isPreset }) => isPreset)
  const customLayouts = layouts.filter(({ isPreset }) => !isPreset)

  const handleSelect = (layoutId: string) => {
    onLayoutSelect(layoutId)
    setIsOpen(false)
  }

  const handleSystemLayoutDefault = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    const customDefaultLayout = customLayouts.find(({ isDefault }) => isDefault)

    if (customDefaultLayout) void handleSetDefaultLayout(customDefaultLayout.id, false)
  }

  return (
    <Popover
      direction='bottomRight'
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      content={
        <Box maxWidth={404}>
          <PopoverCard maxHeight={356}>
            <Box
              flex
              direction='column'
              maxHeight='22.25rem'
              overflowX='hidden'
              overflowY='hidden'
              className={styles.cardBody}
            >
              <Box
                flex='1 1 auto'
                minHeight={0}
                direction='column'
                overflowX='hidden'
                overflowY='hidden'
              >
                <PopoverList ref={popoverListRef}>
                  {presetLayouts.map((layout) => {
                    const isDefault = layout.id === defaultLayoutId

                    return (
                      <ListItem
                        hoverDisabled
                        key={layout.id}
                        title={layout.name}
                        subtitle={layout.description}
                        onClick={() => handleSelect(layout.id)}
                        startSlot={{
                          icon: { icon: HouseIcon, color: 'text-white' },
                          shape: 'squircle',
                          bg: 'surface-primary-high-em',
                          size: 'x40',
                        }}
                        endSlot={{
                          iconButtons: [
                            {
                              'aria-label': t('Dashboard.Make this layout default'),
                              variant: 'ghost',
                              icon: getLayoutIcon(isDefault),
                              disabled: !customLayouts.some(({ isDefault }) => isDefault),
                              onClick: handleSystemLayoutDefault,
                            },
                          ],
                        }}
                      />
                    )
                  })}

                  {customLayouts.length > 0 && (
                    <>
                      <Box flex align='center' gap='x8' p='x8' borderBottom='outline-low-em'>
                        <Body2 medium color='text-high-em'>
                          {t('Dashboard.CustomLayouts')}
                        </Body2>
                      </Box>

                      {customLayouts.map((layout, index) => (
                        <LayoutItem
                          index={index}
                          key={layout.id}
                          layout={layout}
                          currentLayoutId={currentLayoutId}
                          onSelect={handleSelect}
                          onEditMode={onEditMode}
                          onDeleteLayout={onDeleteLayout}
                          openLayoutActionModal={openLayoutActionModal}
                          defaultLayoutId={defaultLayoutId}
                          handleSetDefaultLayout={handleSetDefaultLayout}
                        />
                      ))}
                    </>
                  )}
                </PopoverList>
              </Box>

              {canAddMore && (
                <Box
                  flex
                  align='center'
                  pt='x8'
                  fullWidth
                  className={classNames(styles.addNewLayoutButtonWrapper, {
                    [styles.hasShadow]: canScrollDown,
                  })}
                >
                  <Button.Primary
                    size='md'
                    iconLeft={PlusIcon}
                    fullWidth
                    onClick={() => {
                      setIsOpen(false)
                      onCreateNew()
                    }}
                  >
                    {t('Add New')}
                  </Button.Primary>
                </Box>
              )}
            </Box>
          </PopoverCard>
        </Box>
      }
    >
      <Button.Secondary
        size='sm'
        iconLeft={MetricsIcon}
        iconRight={isOpen ? ChevronIcon.Up : ChevronIcon.Down}
        onClick={() => setIsOpen((open) => !open)}
      >
        {currentLayout?.name || t('Dashboard.SelectLayout')}
      </Button.Secondary>
    </Popover>
  )
}
